import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton } from '@mui/material';

import DeleteIcon from '~/assets/icons/svgs/trash-bin.svg?react';
import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import { imagesSlice, updateImagesSiteThunk } from '~/store/reducers/previewScreens/images';
import { getImagesReducer } from '~/store/reducers/previewScreens/images/selectors';
import { isMaxLength } from '~/utils/common';

const mockTextFieldData = [
  { label: 'imageAlbum', value: '', id: 'header', requestId: 'title', placeholder: 'p1', maxLength: 100 },
  {
    label: 'description',
    value: '',
    id: 'description',
    requestId: 'description',
    placeholder: 'p2',
    maxLength: 200,
  },
  {
    label: 'website',
    value: '',
    id: 'website',
    requestId: 'website',
    placeholder: 'p3',
    validation: true,
    maxLength: 2000,
  },
];

const { actions } = imagesSlice;

export default function Content() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { buttons, micrositeData, imageInformation } = useSelector(getImagesReducer);
  const isDesktop = useSelector(getIsDesktop);

  const buttonsForRequest = buttons.map((item, index) => ({ ...item, index }));

  const handleLink = (id, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (id) {
      const newBtnList = [...buttonsForRequest].filter((btn) => btn.id !== id);

      handleChangeButtonsList(newBtnList, dispatch(actions.setButtons(newBtnList)));

      return;
    }
    const newBtnList = [...buttonsForRequest, { id: buttons?.at(-1) ? buttons.at(-1)?.id + 1 : 1, text: '', url: '' }];

    handleChangeButtonsList(newBtnList, dispatch(actions.setButtons(newBtnList)));
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(buttons, result.source.index, result.destination.index);

    handleChangeButtonsList(items, dispatch(actions.setButtons(items)));
  };

  const handleChangeButtonsList = (items, dispatchHandler) => {
    dispatch(
      updateImagesSiteThunk({
        id: micrositeData?.id,
        body: {
          buttons: items.map((item, index) => ({ ...item, position: index })),
        },
        ...(dispatchHandler && { dispatchHandler: () => dispatchHandler() }),
      }),
    );
  };

  const handleChangeDetails = (id, requestId) => {
    dispatch(
      updateImagesSiteThunk({
        id: micrositeData?.id,
        body: {
          [requestId]: imageInformation[id],
        },
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="content">
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('template6:imageInfo')}
        details={
          <>
            {mockTextFieldData.map((item, index) => {
              return item.id === 'website' ? (
                <UrlTextField
                  requireProtocol={false}
                  urlValue={imageInformation[item.id]}
                  sx={{ marginBottom: '12px' }}
                  key={index}
                  required={false}
                  label={t('labels:website')}
                  placeholder={t(`template6:placeholder:${item.placeholder}`)}
                  inputProps={{ maxLength: 2000 }}
                  onChange={(e) => {
                    dispatch(actions.setImageInformation({ field: item.id, value: e.target.value }));
                  }}
                  defaultValue={imageInformation[item.id]}
                  onBlur={({ isValid }) => isValid && handleChangeDetails(item.id, item.requestId)}
                />
              ) : (
                <TextInput
                  key={index}
                  onChange={(e) => dispatch(actions.setImageInformation({ field: item.id, value: e.target.value }))}
                  onBlur={() => handleChangeDetails(item.id, item.requestId)}
                  sx={{ marginBottom: '12px' }}
                  fullWidth
                  label={t(`other:${item?.label}`)}
                  placeholder={t(`template6:placeholder:${item.placeholder}`)}
                  defaultValue={imageInformation[item.id]}
                  helperText={isMaxLength(imageInformation[item.id], item.maxLength)}
                  inputProps={{ maxLength: item.maxLength }}
                  error={item.validation}
                />
              );
            })}

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{
                      margin: '16px 0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    {buttons?.map((item, index) => {
                      return (
                        <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                          {(provided) => {
                            return (
                              <Box ref={provided.innerRef} {...provided.draggableProps}>
                                <Accordion
                                  defaultExpanded
                                  variant="draggableItem"
                                  summary={
                                    <>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                          <Text variant="t16Regular">
                                            {t('other:button')}
                                            {index + 1}
                                          </Text>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'right',
                                            alignItems: 'center',
                                            gap: '24px',
                                            marginRight: '24px',
                                          }}
                                        >
                                          <IconButton disableRipple {...provided.dragHandleProps}>
                                            <MenuIcon />
                                          </IconButton>
                                          <IconButton disableRipple onClick={(e) => handleLink(item.id, e)}>
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </>
                                  }
                                  details={
                                    <Box
                                      display="flex"
                                      sx={{ gap: '16px', flexDirection: isDesktop ? 'row' : 'column' }}
                                    >
                                      <TextInput
                                        fullWidth
                                        label={t('other:btnTxt')}
                                        placeholder={t('template2:writeHere')}
                                        onChange={(e) => {
                                          dispatch(
                                            actions.setButtonsDetail({
                                              field: 'text',
                                              index,
                                              value: e.target.value,
                                            }),
                                          );
                                        }}
                                        onBlur={() => handleChangeButtonsList(buttons)}
                                        defaultValue={item.text}
                                        error={!item.text}
                                        helperText={isMaxLength(item?.text, 50) || (!item.text && t('inputs:required'))}
                                        inputProps={{ maxLength: 50 }}
                                      />
                                      <UrlTextField
                                        requireProtocol={false}
                                        urlValue={item.url}
                                        placeholder="https://"
                                        label={t('labels:url')}
                                        onChange={(e) => {
                                          dispatch(
                                            actions.setButtonsDetail({
                                              field: 'url',
                                              index,
                                              value: e.target.value,
                                            }),
                                          );
                                        }}
                                        defaultValue={item.url}
                                        onBlur={({ isValid }) => isValid && handleChangeButtonsList(buttons)}
                                      />
                                    </Box>
                                  }
                                />
                              </Box>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            <Button type="primary-outlined" size="xs" startIcon={<AddIcon />} onClick={() => handleLink()}>
              {t('template6:addBtn')}
            </Button>
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
